import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDay, faChevronLeft, faChevronRight, faXmark } from "@fortawesome/free-solid-svg-icons";
import "./DatePicker.css";

const pad = (n: number) => String(n).padStart(2, "0");
const toISO = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

const MONTHS_RO = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW_RO = ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];
const DOW_EN = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

// Custom calendar date picker. value/onChange use "YYYY-MM-DD" strings.
const DatePicker = ({
    value,
    onChange,
    placeholder = "—",
    label,
    en = false,
}: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    en?: boolean;
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Which month the grid is showing. Defaults to the selected value's month, else today.
    const initial = value ? new Date(value) : new Date();
    const [view, setView] = useState({ y: initial.getFullYear(), m: initial.getMonth() });

    useEffect(() => {
        if (value) { const d = new Date(value); setView({ y: d.getFullYear(), m: d.getMonth() }); }
    }, [value]);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const months = en ? MONTHS_EN : MONTHS_RO;
    const dow = en ? DOW_EN : DOW_RO;

    // Build the day grid (Monday-first) with leading blanks.
    const cells = useMemo(() => {
        const first = new Date(view.y, view.m, 1);
        const startBlanks = (first.getDay() + 6) % 7; // convert Sun=0 to Mon-first
        const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
        const arr: (number | null)[] = [];
        for (let i = 0; i < startBlanks; i++) arr.push(null);
        for (let d = 1; d <= daysInMonth; d++) arr.push(d);
        return arr;
    }, [view]);

    const today = new Date();
    const isToday = (d: number) => today.getFullYear() === view.y && today.getMonth() === view.m && today.getDate() === d;
    const isSelected = (d: number) => value === toISO(view.y, view.m, d);

    const shiftMonth = (delta: number) => {
        setView((v) => {
            const m = v.m + delta;
            if (m < 0) return { y: v.y - 1, m: 11 };
            if (m > 11) return { y: v.y + 1, m: 0 };
            return { y: v.y, m };
        });
    };

    const display = value ? new Date(value).toLocaleDateString(en ? "en-GB" : "ro-RO") : "";

    return (
        <div className="zv-datepicker" ref={ref}>
            {label ? <span className="zv-dp-label">{label}</span> : null}
            <button type="button" className={`zv-dp-trigger ${open ? "open" : ""}`} onClick={() => setOpen((o) => !o)}>
                <FontAwesomeIcon icon={faCalendarDay} className="zv-dp-ico" />
                <span className={value ? "" : "zv-dp-placeholder"}>{value ? display : placeholder}</span>
                {value ? (
                    <FontAwesomeIcon
                        icon={faXmark}
                        className="zv-dp-clear"
                        onClick={(e) => { e.stopPropagation(); onChange(""); }}
                    />
                ) : null}
            </button>

            {open ? (
                <div className="zv-dp-pop">
                    <div className="zv-dp-nav">
                        <button type="button" onClick={() => shiftMonth(-1)}><FontAwesomeIcon icon={faChevronLeft} /></button>
                        <span className="zv-dp-title">{months[view.m]} {view.y}</span>
                        <button type="button" onClick={() => shiftMonth(1)}><FontAwesomeIcon icon={faChevronRight} /></button>
                    </div>
                    <div className="zv-dp-grid zv-dp-dow">
                        {dow.map((d) => <span key={d} className="zv-dp-dowcell">{d}</span>)}
                    </div>
                    <div className="zv-dp-grid">
                        {cells.map((d, i) => d === null
                            ? <span key={`b${i}`} className="zv-dp-cell blank" />
                            : (
                                <button
                                    type="button"
                                    key={d}
                                    className={`zv-dp-cell ${isSelected(d) ? "selected" : ""} ${isToday(d) ? "today" : ""}`}
                                    onClick={() => { onChange(toISO(view.y, view.m, d)); setOpen(false); }}
                                >
                                    {d}
                                </button>
                            ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default DatePicker;
